import { useEffect, useState } from "react";
import { BotIcon, FileIcon } from "lucide-react";

import { Column, LoadingIndicator, Row, Text } from "@/components";

import { quoMessageStatusToLabel } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { getStableQueuedLabel } from "@/lib/quoMessages";
import { cn } from "@/lib/utils";
import {
  QuoMessageDirection,
  QuoMessageResponse,
  QuoMessageStatus,
} from "@/types";

const AgentBadge = () => (
  <Row className="absolute -top-2 right-1 z-10 h-5 w-5 items-center justify-center rounded-full bg-white text-primary-700 ring-1 ring-primary-300">
    <BotIcon className="h-3 w-3" />
  </Row>
);

export const MessageBubble = ({
  isFirst,
  isLast,
  message,
}: {
  isFirst: boolean;
  isLast: boolean;
  message: QuoMessageResponse;
}) => {
  /* React Hooks */
  const [dotCount, setDotCount] = useState(1);

  const isOutgoing = message.direction === QuoMessageDirection.OUTGOING;
  const isAgentMessage = isOutgoing && message.sentByAgent;
  const isQueued = message.status === QuoMessageStatus.QUEUED;
  const queuedLabel = getStableQueuedLabel(
    `${message.userId.toString()}-${message.body}`,
  );

  // useEffect: animate "…" tail on queued status.
  useEffect(() => {
    if (!isQueued) return;
    const dotsTimer = window.setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 350);
    return () => {
      window.clearInterval(dotsTimer);
    };
  }, [isQueued]);

  /* Render */
  const renderBodyBubble = () => {
    if (!message.body) return null;
    return (
      <div
        className={cn(
          "relative max-w-full rounded-2xl px-4 py-2.5",
          isAgentMessage &&
            "bg-gradient-to-br from-primary-900/95 to-blue-950/95 text-white shadow-md shadow-primary-950/20 ring-1 ring-inset ring-white/25 backdrop-blur-sm",
          isOutgoing && !isAgentMessage && "bg-primary-900 text-white",
          !isOutgoing && "bg-white text-slate-600 shadow-sm",
          isFirst && isLast && !message.media?.length
            ? ""
            : isFirst
              ? "rounded-b-lg"
              : isLast && !message.media?.length
                ? "rounded-t-lg"
                : "rounded-lg",
        )}
      >
        {message.sentByAgent && <AgentBadge />}
        <Text
          className={cn(
            "whitespace-pre-wrap text-sm font-normal [overflow-wrap:anywhere]",
            isAgentMessage && "text-white",
            isOutgoing && !isAgentMessage && "text-white",
            !isOutgoing && "text-slate-600",
          )}
        >
          {message.body}
        </Text>
      </div>
    );
  };

  const renderMedia = () =>
    message.media?.map((item, index) => {
      // Badge attaches to the first visual element of the message; when there's
      // a body, it lives there. Otherwise it lands on media[0].
      const isFirstItem = isFirst && !message.body && index === 0;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const isLastItem = isLast && index === message.media!.length - 1;
      const showAgentBadge =
        message.sentByAgent && !message.body && index === 0;

      if (item.type.startsWith("image/")) {
        return (
          <a
            key={index}
            className="relative"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {showAgentBadge && <AgentBadge />}
            <img
              alt="Media attachment"
              className="max-h-48 max-w-full rounded-lg object-contain"
              src={item.url}
            />
          </a>
        );
      }

      return (
        <div
          key={index}
          className={cn(
            "relative max-w-full rounded-2xl px-4 py-2.5",
            isAgentMessage &&
              "bg-gradient-to-br from-primary-900/95 to-blue-950/95 text-white shadow-md shadow-primary-950/20 ring-1 ring-inset ring-white/25 backdrop-blur-sm",
            isOutgoing && !isAgentMessage && "bg-primary-900 text-white",
            !isOutgoing && "bg-white text-slate-600 shadow-sm",
            isFirstItem && isLastItem
              ? ""
              : isFirstItem
                ? "rounded-b-lg"
                : isLastItem
                  ? "rounded-t-lg"
                  : "rounded-lg",
          )}
        >
          {showAgentBadge && <AgentBadge />}
          <a
            className={cn(
              "flex min-w-0 items-center gap-2 text-sm underline",
              isAgentMessage && "text-white/80",
              isOutgoing && !isAgentMessage && "text-white/80",
              !isOutgoing && "text-slate-500",
            )}
            href={item.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <FileIcon className="h-3.5 w-3.5 shrink-0" />
            <Text
              truncate
              className="text-sm font-normal text-inherit underline"
            >
              {item.type || "File attachment"}
            </Text>
          </a>
        </div>
      );
    });

  const renderFooter = () => {
    if (!isLast) return null;
    return (
      <Row
        className={cn(
          "mt-1 items-center gap-1.5 px-1",
          isOutgoing && "justify-end",
        )}
      >
        <Text className="text-xs font-medium text-slate-500">
          {dates.formatTime(message.sentAt)}
        </Text>
        {isOutgoing && (
          <Row
            className={cn(
              "items-center gap-1 text-xs font-medium",
              message.status === QuoMessageStatus.UNDELIVERED
                ? "text-red-400"
                : "text-slate-500",
            )}
          >
            {isQueued && (
              <LoadingIndicator className="h-2.5 w-2.5" variant="xsmall" />
            )}
            {isQueued ? (
              <>
                <Text className="text-xs font-medium text-inherit">
                  {queuedLabel}
                </Text>
                <Text className="inline-block w-[3ch] text-left text-xs font-medium text-inherit">
                  {".".repeat(dotCount)}
                </Text>
              </>
            ) : (
              <Text className="text-xs font-medium text-inherit">
                {quoMessageStatusToLabel[message.status]}
              </Text>
            )}
          </Row>
        )}
      </Row>
    );
  };

  const render = () => (
    <Row className={cn("w-full", isOutgoing ? "justify-end" : "justify-start")}>
      <Column
        className={cn(
          "min-w-0 gap-0.5",
          isOutgoing ? "ml-[10%] items-end" : "mr-[10%] items-start",
        )}
      >
        {renderBodyBubble()}
        {renderMedia()}
        {renderFooter()}
      </Column>
    </Row>
  );

  return render();
};
